<template>
  <div class="mx-6 my-4">
    <div class="md:flex md:items-center md:justify-between md:space-x-5 pb-2">
      <div class="flex items-center space-x-5">
        <div class="flex-shrink-0">
          <div class="relative bg-violet-600/70 rounded-xl p-1">
            <mdicon name="file-tree-outline" size="62" class="text-violet-100" aria-hidden="true"></mdicon>
          </div>
        </div>
        <div class="">
          <h1 class="text-2xl font-bold text-gray-900">{{ categoriesName }}</h1>
          <div class="flex items-center">
            <div class="">
              ID: <span class="font-medium text-gray-600">{{ categoriesId }}</span>
            </div>
            <ClipboardCopyButton @click="copyId"></ClipboardCopyButton>
          </div>
        </div>
      </div>

      <!-- Control Groups -->

      <div
        v-if="rbac.canEditCategories"
        class="flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3"
      >
        <button type="button" class="flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50" @click="onExportCatMd">
          <mdicon name="download-outline" size="18" class="mr-1 text-gray-500" aria-hidden="true"></mdicon>
          Export Md
        </button>

        <button type="button" class="flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50" @click="onExportCat">
          <mdicon name="download-outline" size="18" class="mr-1 text-gray-500" aria-hidden="true"></mdicon>
          Export
        </button>

        <button type="button" class="mr-2 flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50" @click="onCloneCat">
          <mdicon name="content-duplicate" size="18" class="mr-1 text-gray-500" aria-hidden="true"></mdicon>
          Clone
        </button>

        <button
          type="button"
          :class="[
            categoriesStore.catIsChanged ? 'bg-indigo-600 text-white hover:bg-indigo-500' : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
            'mr-2 inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
          ]"
          @click="onSaveCat"
        >
          <CheckIcon class="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
          Save
        </button>
      </div>

      <!--<div class="flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
        <button
          class="flex items-center gap-x-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          @click="onExport"
        >
          <ArrowDownCircleIcon class="-ml-1.5 h-5 w-5" aria-hidden="true" />
          Report
        </button>
      </div>-->
    </div>

    <TabsResponsive v-model:tabs="tabs" v-model:show-all="showAllTabsContent"></TabsResponsive>

    <h2 v-if="showAllTabsContent" class="text-xl font-semibold leading-7 text-gray-600 border-b border-gray-300 py-2">Summary</h2>
    <CategoriesSummary v-if="tabs[0].active"></CategoriesSummary>
    <CategoriesTreeEditor v-if="tabs[1].active"></CategoriesTreeEditor>
    <!--
    <h2 v-if="showAllTabsContent" class="text-xl font-semibold leading-7 text-gray-600 border-b border-gray-300 py-2">Edit</h2>
    <CategoriesMdEditor v-if="tabs[1].active"></CategoriesMdEditor>
    -->

    <ConfirmDialog group="cloneCat">
      <template #message="slotProps">
        <div class="flex flex-col gap-2 w-96">
          <label for="name">Name</label>
          <InputText id="name" class="" v-model="categoriesCloneName" aria-describedby="name-help" />
          <small id="name-help">Enter the name for the new Categories</small>
        </div>
      </template>
    </ConfirmDialog>
  </div>
</template>

<script setup>
  import { onMounted, onBeforeMount, ref, computed, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useLayoutStore } from '@/store/LayoutStore';
  import { useRbacStore } from '@/store/RBACStore';
  import { useCategoriesStore } from '@/store/CategoriesStore';
  import { useToast } from 'vue-toastification';
  import { useProgress } from '@/hooks/useProgress';
  import { useDownload } from '@/hooks/useDownload';
  import TabsResponsive from '@/components/Tabs/TabsResponsive.vue';
  import CategoriesSummary from '@/components/Categories/editors/CategoriesSummary.vue';
  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import { pathOr } from 'ramda';
  import { useMd } from '@/hooks/useMd';
  import ClipboardCopyButton from '@/components/Buttons/ClipboardCopyButton.vue';
  import DropdownSelectButton from '@/components/Buttons/DropdownSelectButton.vue';
  import { CheckIcon } from '@heroicons/vue/20/solid';
  import Button from 'primevue/button';
  import CategoriesMdEditor from '@/components/Categories/editors/CategoriesMdEditor.vue';
  import CategoriesTreeEditor from '@/components/Categories/editors/CategoriesTreeEditor.vue';
  import ConfirmDialog from 'primevue/confirmdialog';
  import { useConfirm } from 'primevue/useconfirm';
  import InputText from 'primevue/inputtext';

  //dayjs.extend(utc);

  const route = useRoute();
  const router = useRouter();
  const layout = useLayoutStore();
  const rbac = useRbacStore();
  const categoriesStore = useCategoriesStore();
  const md = useMd();
  const progress = useProgress();
  const toast = useToast();
  const download = useDownload();
  const confirm = useConfirm();

  const categoriesId = computed(() => categoriesStore.catId);
  const categoriesName = computed(() => categoriesStore.catName);
  const categoriesCloneName = ref('');

  const tabs = ref([
    { name: 'Summary', href: '#', icon: 'chart-box-outline', active: true, hidden: false },
    { name: 'Categories', href: '#', icon: 'file-tree-outline', active: false, hidden: false },
    //{ name: 'Categories Editor', href: '#', icon: 'text-box-edit-outline', active: false, hidden: false }, // todo Edit as text ?
  ]);
  const tabsHidden = computed(() => [false, false]);
  const showAllTabsContent = ref(false);

  // todo Export report
  function onExport() {
    //const reports = ConversationDetailsReport.generateResultReports(conversation.value);
    //downloadReport(`Conversation_${conversationId.value}`, reports.reportHtml, 'text/html');
  }

  function downloadReport(filename, report, mimeType) {
    // credit: https://www.bitdegree.org/learn/javascript-download
    let text = report;
    let element = document.createElement('a');
    element.setAttribute('href', `data:${mimeType};charset=utf-8,` + encodeURIComponent(text));
    element.setAttribute('download', filename);
    //element.setAttribute('target', '_blank');
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  function copyId() {
    navigator.clipboard.writeText(categoriesId.value);
  }

  /*
  watch(
    () => conversation.value,
    async (newVal) => {
      if (conversation.value?.kbId && conversation.value?.kbId !== '') {
        kbId.value = conversation.value.kbId;
      }
      // fallback to pre-loading bot and getting kbId from bot spec
      kbId.value = await botsStore.getBotKbId(conversation.value?.botId);
    },
  );
  */

  function onExportCat() {
    const specContent = JSON.stringify(categoriesStore.catSpec, null, 2);
    download.downloadFile(`Categories_${categoriesStore.catId}`, specContent, 'application/json');
  }

  function categoriesToMd(categories, level = '') {
    if (!Array.isArray(categories) || categories.length === 0) {
      return '';
    }
    let mdText = '';
    const currentLevel = level + '#';
    categories.forEach((cat) => {
      mdText += `${currentLevel} ${cat.name}\n`;
      if (cat.description && cat.description !== '') {
        mdText += `${cat.description}\n`;
      }
      if ('categories' in cat) {
        mdText += categoriesToMd(cat.categories, currentLevel);
      }
    });
    return mdText;
  }

  function onExportCatMd() {
    const mdContent = categoriesToMd(categoriesStore.catSpec.categories);
    download.downloadFile(`Categories_${categoriesStore.catId}_markdown.md`, mdContent, 'text/markdown');
  }

  function onCloneCat() {
    categoriesCloneName.value = `${categoriesStore.catName} (Copy)`;
    confirm.require({
      group: 'cloneCat',
      header: 'Clone Categories',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      rejectClass: 'p-button-outlined p-button-sm',
      acceptClass: 'p-button-sm',
      rejectLabel: 'Cancel',
      acceptLabel: 'Clone',
      accept: () => {
        const newCatId = categoriesStore.cloneCatInPlace(categoriesCloneName.value);
        router.push(`/datasets/categories/edit/${newCatId}`);
      },
      reject: () => {
        console.log(`Categories clone canceled`);
      },
    });
  }

  async function onSaveCat() {
    await categoriesStore.beforeSaveCatDocument();
    progress.startProgress({ blockUI: true, spinner: true });
    const saveResult = await categoriesStore.saveCat();
    progress.stopProgress();
    if (saveResult.success) {
      toast.success(`Categories saved successfully`);
    } else {
      toast.error(`Error saving categories: ${saveResult.error}`);
    }
  }

  async function initCategoriesEditor() {
    let id = route.params?.id || null;
    if (!id) {
      console.log(`ERROR: Categories ID must be specified`);
      toast.error(`Categories ID must be specified`);
      return;
    }

    /* todo reconsider - may want to extend sidebar for categories editor
    const kbEditorRoutePrefix = `/kb/editor/${id}`; //route.path.split('/').slice(0, 4).join('/');
    const kbEditorSidebarSectionName = computed(() => kbStore.kbName || 'Knowledge Base');
    const kbShowRouteForSavedKb = computed(() => !kbStore.kbIsNew);
    sidebarStore.setExtraItems(kbEditorRoutePrefix, [
      { title: kbEditorSidebarSectionName, type: 'sectionTitle' },
      { title: 'Documents', show: kbShowRouteForSavedKb, link: kbEditorRoutePrefix + '/documents', icon: 'file-document-multiple-outline', iconSize: 16, iconClass: 'bg-violet-800 text-white rounded' },
      { title: 'Search', show: kbShowRouteForSavedKb, link: kbEditorRoutePrefix + '/search', icon: 'database-search-outline', iconSize: 16, iconClass: 'bg-violet-800 text-white rounded' },
      { title: 'Settings', link: kbEditorRoutePrefix + '/settings', icon: 'cog-outline', iconSize: 16, iconClass: 'bg-violet-800 text-white rounded' },
    ]);
    */
  }

  onBeforeMount(async () => {
    let id = route.params?.id || null;
    if (!id) {
      console.log(`ERROR: Categories ID must be specified`);
      return;
    }
    await categoriesStore.loadCat(id);
  });

  onMounted(async () => {
    await initCategoriesEditor();
  });
</script>
